import { useDrop, useDrag } from 'react-dnd';
import { Check, Mail } from 'lucide-react';
import type { Post, CommunicationChannel } from '../../../types';
import { defaultChannels } from '../../../config/channels';
import { channelIcons, ChannelSvgIcon } from '../../../utils/Constants';
import { isPast as isDatePast } from '../../../utils/dateUtils';
import { useLanguage } from '../../../contexts/LanguageContext';

interface CalendarDayProps {
  day: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected?: boolean;
  posts: Post[];
  communicationChannels: CommunicationChannel[];
  onPostClick: (post: Post) => void;
  onUpdatePost: (post: Post, scheduledDate?: Date) => void;
  onDayClick?: (day: Date) => void;
}

// Post label component with drag (only if not in past)
function PostLabel({
  post,
  communicationChannels,
  onClick,
  isPast,
  count = 1,
}: {
  post: Post;
  communicationChannels: CommunicationChannel[];
  onClick: (e: React.MouseEvent) => void;
  isPast: boolean;
  count?: number;
}) {
  const { t } = useLanguage();
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'queuePost',
    item: post,
    canDrag: !isPast,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [isPast]);

  const channel = communicationChannels.find((c) => c.id === post.channelId);
  const channelConfig = defaultChannels.find((c) => c.name === channel?.network);

  if (post.isNewsletter) {
    return (
      <div
        ref={(node) => { if (!isPast) drag(node); }}
        onClick={onClick}
        className={`px-2.5 py-1 rounded-full flex items-center justify-center gap-1 transition-opacity relative ${ isPast ? 'opacity-60' : 'cursor-pointer hover:opacity-80'
        } ${
          isDragging ? 'opacity-30' : ''
        }`}
        style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-light)', backgroundColor: 'var(--color-primary)' }}
      >
        <Mail className="w-2.5 h-2.5" />
        <span>{t.menu.newsletter}</span>
        {isPast && (
          <div
            className="absolute -top-1 -right-1 w-3 h-3 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-success)', marginLeft: '1.4rem', marginBottom: '0.4rem' }}
          >
            <Check className="w-2 h-2" style={{ color: 'var(--color-text-light)' }} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={(node) => { if (!isPast) drag(node); }}
      onClick={onClick}
      className={`rounded-full flex items-center justify-center transition-opacity relative ${
        isPast ? 'opacity-60' : 'cursor-pointer hover:opacity-80'
      } ${
        isDragging ? 'opacity-30' : ''
      }`}
    >
      <ChannelSvgIcon network={channel?.network} size={18} fallbackClassName={channelConfig ? `${channelConfig.color} ${channelConfig.textColor}` : ''} />
      {count > 1 && (
        <div
          className="absolute -top-1 -right-1 w-3 h-3 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'var(--color-primary)', color: 'white', fontSize: '7px', fontWeight: 700, marginLeft: '1.4rem', marginBottom: '0.4rem' }}
        >
          {count}
        </div>
      )}
      {isPast && count === 1 && (
        <div
          className="absolute -top-1 -right-1 w-3 h-3 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'var(--color-success)', marginLeft: '1.4rem', marginBottom: '0.4rem' }}
        >
          <Check className="w-2 h-2" style={{ color: 'var(--color-text-light)' }} />
        </div>
      )}
    </div>
  );
}

export function CalendarDay({
  day,
  isCurrentMonth,
  isToday,
  isSelected,
  posts,
  communicationChannels,
  onPostClick,
  onUpdatePost,
  onDayClick,
}: CalendarDayProps) {
  const isPast = isDatePast(day);

  const [{ isOver }, drop] = useDrop(() => ({
    accept: ['post', 'queuePost'],
    canDrop: () => !isPast, // Ne pas permettre de drop sur une date passée
    drop: (item: Post & { currentChannelId?: string }) => {
      // Update the post's scheduled date to this day
      const newScheduledDate = new Date(day);
      // Keep the same time, or default to 10:00
      if (item.scheduledDate) {
        const oldDate = new Date(item.scheduledDate);
        newScheduledDate.setHours(oldDate.getHours(), oldDate.getMinutes());
      } else {
        newScheduledDate.setHours(10, 0);
      }
      
      // Use currentChannelId if provided (from drag state), otherwise use item.channelId
      const updatedPost = item.currentChannelId 
        ? { ...item, channelId: item.currentChannelId }
        : item;
      
      onUpdatePost(updatedPost, newScheduledDate);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver() && monitor.canDrop(),
    }),
  }), [day, isPast]);

  return (
    <div
      ref={(node) => { drop(node); }}
      onClick={() => onDayClick?.(day)}
      className={`min-h-[70px] p-1 border rounded-lg flex flex-col gap-0.5 ${isPast ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      style={{
        backgroundColor: !isCurrentMonth 
          ? 'var(--color-surface)' 
          : isPast 
          ? 'var(--color-surface)'
          : isSelected
          ? 'rgba(85, 0, 255, 0.1)'
          : isToday || isOver
          ? 'rgba(85, 0, 255, 0.05)'
          : 'var(--color-background)',
        borderColor: !isCurrentMonth
          ? 'var(--color-border)'
          : isPast
          ? 'var(--color-border)'
          : isSelected
          ? 'var(--color-primary)'
          : isToday || isOver
          ? 'var(--color-primary)'
          : 'var(--color-border)',
        borderWidth: isSelected ? 2 : 1,
      }}
    >
      <div
        className="text-xs text-center pb-0.5"
        style={{
          color: !isCurrentMonth
            ? 'var(--color-text-secondary)'
            : isPast
            ? 'var(--color-text-secondary)'
            : isToday
            ? 'var(--color-primary)'
            : 'var(--color-text-primary)',
          fontWeight: isToday ? 600 : 400
        }}
      >
        {day.getDate()}
      </div>
      <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
        {Object.entries(
          posts.reduce((acc, post) => {
            const key = post.isNewsletter ? `newsletter_${post.id}` : (post.channelId ?? 'none');
            if (!acc[key]) acc[key] = [];
            acc[key].push(post);
            return acc;
          }, {} as Record<string, Post[]>)
        ).map(([key, groupPosts]) => (
          <PostLabel
            key={key}
            post={groupPosts[0]}
            communicationChannels={communicationChannels}
            onClick={(e) => {
              e.stopPropagation();
              if (groupPosts.length === 1) {
                onPostClick(groupPosts[0]);
              } else {
                onDayClick?.(day);
              }
            }}
            isPast={isPast}
            count={groupPosts[0].isNewsletter ? 1 : groupPosts.length}
          />
        ))}
      </div>
    </div>
  );
}